<template>
	<div>
		<div class="position-relative">
			<table
				class="table table-fixed table-hover"
				:class="{ clickable: clickableRows, 'table-striped': striped }"
			>
				<thead>
					<tr>
						<th
							v-for="(head) in fields"
							:key="head.key"
							:class="head.class || ''"
						>
							<slot
								v-if="$scopedSlots[`th-${head.key}`]"
								:name="`th-${head.key}`"
							></slot>
							<template v-else>
								{{ head.label }}
								<template v-if="sort[head.key] !== undefined">
									<span class="sort" @click="onToggleSort($event, head.key)">
										<CIcon :name="sort[head.key] === SORT_TYPE.ASC ? 'cil-sort-ascending' : 'cil-sort-descending'" />
									</span>
								</template>
							</template>
						</th>
					</tr>
				</thead>
				<tbody v-if="!isLoading">
					<tr
						v-for="itemData in items"
						:key="itemData.key"
					>
						<template v-for="(item, key) in itemData">
							<router-link
								v-if="nameRoute && paramsRoute && paramsKey && !hideColumns.includes(key)"
								:key="key"
								:to="{
									name: nameRoute,
									params: { [paramsRoute]: itemData[paramsKey] }
								}"
								:class="[
									getCellClass(key),
									{
										'border-left': [
											...borderLeft
										].includes(getCellClass(key))
									},
									{
										'border-right': [
											...borderRight
										].includes(getCellClass(key))
									}
								]"
								class="td-hyperlink"
							>
								<slot
									v-if="$scopedSlots[key]"
									:name="key"
									:item="item"
								></slot>
								<template v-else>
									{{ defaultValue(item, '-') }}
								</template>
							</router-link>
							<td
								v-else-if="!hideColumns.includes(key)"
								:key="key"
								:class="[
									getCellClass(key),
									{
										'border-left': [
											...borderLeft
										].includes(getCellClass(key))
									},
									{
										'border-right': [
											...borderRight
										].includes(getCellClass(key))
									}
								]"
							>
								<slot
									v-if="$scopedSlots[key]"
									:name="key"
									:item="item"
								></slot>
								<template v-else>
									{{ defaultValue(item, '-') }}
								</template>
							</td>
						</template>
						<slot name="action"></slot>
					</tr>
				</tbody>
			</table>
		</div>
		<BaseLoading v-if="isLoading" />
		<slot v-else-if="!items.length" name="no-items-view"></slot>
		<CPagination
			v-if="pagination.pages > 1"
			:active-page="pagination.activePage"
			:pages="pagination.pages"
			class="mr-4 sticky-with-body-scroll-left"
			align="end"
			show-dots
			@update:activePage="handlePaginationClick"
		/>
	</div>
</template>

<script>
import { defaultValue } from '../helpers/utils';
import { SORT_TYPE } from '../enums/sort';

export default {
	name: 'BaseTable',

	props: {
		items: {
			type: [Object, Array],
			default: null,
		},
		fields: {
			type: Array,
			default: () => [],
		},
		pagination: {
			type: Object,
			default: () => ({}),
		},
		isLoading: {
			type: Boolean,
			default: false,
		},
		clickableRows: {
			type: Boolean,
			default: false,
		},
		striped: {
			type: Boolean,
			default: false,
		},
		hideColumns: {
			type: Array,
			default: () => [],
		},
		sortColumns: {
			type: Object,
			default: () => ({}),
		},
		borderLeft: {
			type: Array,
			default: () => [],
		},
		borderRight: {
			type: Array,
			default: () => [],
		},
		nameRoute: {
			type: String,
			default: null,
		},
		paramsRoute: {
			type: String,
			default: 'id',
		},
		paramsKey: {
			type: String,
			default: 'id',
		},
	},

	data() {
		return {
			SORT_TYPE,
			defaultValue,
			sort: {},
		};
	},

	mounted() {
		// set sort state
		this.setSortState();
	},

	methods: {
		handlePaginationClick(page) {
			this.$emit('onPaginationClick', page);
		},
		getCellClass(key) {
			const found = this.fields.find((field) => field.key === key);
			return found ? found.cellClass : undefined;
		},
		// side effect function
		setSortState() {
			if (this.sortColumns !== undefined) {
				const keys = Object.keys(this.sortColumns);
				keys.forEach((key) => {
					this.$set(this.sort, key, this.sortColumns[key]);
				});
			}
		},
		// side effect function
		onToggleSort(e, key = null) {
			const state = this.sort[key] === SORT_TYPE.ASC
				? SORT_TYPE.DESC
				: SORT_TYPE.ASC;
			// update sort state
			this.sort[key] = state;
			// emit values
			this.$emit('onSortClick', key, state);
		},
	},
};
</script>

<style lang="scss" scoped>
	$border-td: 1px solid #e0e0e0;

	.clickable td {
		cursor: pointer;
	}

	::v-deep .table {
		position: relative;
		border-collapse: separate;
		border-spacing: 0;

		th,
		td,
		tr > a {
			color: $color-black-90;
			font-size: rem(14);
			word-break: break-word;
		}

		thead > tr > th:first-child,
		tbody > tr > td:first-child,
		tbody > tr > a:first-child {
			padding-left: rem(28);
		}

		th {
			position: sticky;
			top: 0;
			background: $color-white;
			border-bottom: 1px solid $color-gray-300;
			z-index: 1;

			.sort {
				cursor: pointer;
				margin-left: rem(8);
				color: $color-black-45;
			}
		}

		td,
		a {
			border-bottom: 1px solid $color-gray-200;
		}

		&-striped {
			th,
			td,
			tr > a {
				border-bottom: none;
			}
		}
	}

	.border {
		&-left {
			border-left: $border-td;
		}

		&-right {
			border-right: $border-td;
		}
	}

	.td-hyperlink {
		display: table-cell;
		padding: rem(12) rem(24);
		font-size: rem(14);
		color: $color-black-90;
		text-decoration: none;
	}
</style>
